import { useRef } from 'react';
import { Component } from '../types/components';

interface ComponentInstance {
  activeCount: number;
  totalCount: number;
}

export default <T extends object>(
  components: Map<string, Component<T>>,
): [(id: string) => boolean, (id: string) => number | undefined, (id: string) => void] => {
  const componentInstancing = useRef<Map<string, ComponentInstance>>(new Map());

  const canInstance = (id: string): boolean => {
    const component = components.get(id);
    if (component) {
      const instances = componentInstancing.current.get(id) ?? {
        activeCount: 0,
        totalCount: 0,
      };
      if (
        !component.instances ||
        (component.instances === 1 && instances.activeCount === 0) ||
        (component.instances > 1 && component.instances >= instances.activeCount + 1)
      ) {
        return true;
      }
    }
    return false;
  };

  const getInstance = (id: string): number | undefined => {
    const component = components.get(id);
    if (component) {
      const instances = componentInstancing.current.get(id) ?? {
        activeCount: 0,
        totalCount: 0,
      };
      instances.activeCount++;
      instances.totalCount++;
      componentInstancing.current.set(id, instances);
      return instances.activeCount;
    }
  };

  const decrementInstance = (id: string) => {
    const component = components.get(id);
    if (component) {
      const instances = componentInstancing.current.get(id) ?? {
        activeCount: 0,
        totalCount: 0,
      };
      instances.activeCount--;
      componentInstancing.current.set(id, instances);
    }
  };

  return [canInstance, getInstance, decrementInstance];
};
